import {
  BiothreatSignalSource,
  type BiothreatSignal as TABiothreatSignal,
} from "@phc/common/gen/threatAnalyticsGen"
import type React from "react"
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react"
import { useUserPreferences } from "../../contexts/UserPreferencesContext"
import { useShowAlertCuration } from "../../hooks/useFeatureFlags"
import usePersistentContext from "../../hooks/usePersistentContext"
import {
  ALL_DISEASES,
  ALL_LOCATIONS,
  ALL_TYPES,
  getCentroidSignals,
} from "./AlertCurationUtils"
import {
  useAlertCandidates,
  useAlertDiseases,
  useTriageMutation,
} from "./useAlertCandidates"
import { useNwssData } from "./useNwssData"
import {
  getDates,
  isRelative,
  safelyParseDate,
  type DynamicDateRange,
} from "../../utils/dates"

export interface AlertCurationFilters {
  dateRange: DynamicDateRange
  aiScoreRange: [number, number]
  disease: string
  type: string
  locationFilter: string
  search: string
  sensitivity: number
}

// temporary patch to merge NWSS and Gdelt signals
export interface BiothreatSignal extends TABiothreatSignal {
  search_term_id?: string
  sub_locations?: string
}

export interface AlertCurationState {
  showAlertCuration: boolean
  isArchivedView: boolean
  toggleArchivedView: () => void
  alertCurationFilters: AlertCurationFilters
  setAlertCurationFilters: (filters: AlertCurationFilters) => void
  diseaseList: string[] | undefined
  isLoading: boolean
  showNullData: boolean
  setShowNullData: (show: boolean) => void
  signals: BiothreatSignal[]
  pinnedSignals: Map<string, BiothreatSignal>
  centroidSignals: BiothreatSignal[]
  triage: (payload: { ids: string[]; action: "archive" | "unarchive" }) => void
  addToPinnedSignals: (signal: BiothreatSignal) => void
  removeFromPinnedSignals: (signalId: string) => void
}

const AlertCurationContext = createContext<AlertCurationState | undefined>(
  undefined
)

export const useAlertCuration = (): AlertCurationState => {
  const context = useContext(AlertCurationContext)
  if (context === undefined) {
    throw new Error(
      "useAlertCuration must be used within AlertCurationProvider"
    )
  }
  return context
}

export const DEFAULT_BIOTHREAT_FILTERS: AlertCurationFilters = {
  dateRange: {
    lookbackUnit: "hours",
    lookbackValue: 24,
  },
  aiScoreRange: [0.3, 1.0],
  disease: ALL_DISEASES,
  type: ALL_TYPES,
  locationFilter: ALL_LOCATIONS,
  search: "",
  sensitivity: 0.88,
}

export const AlertCurationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const showAlertCuration = useShowAlertCuration()

  const [isArchivedView, setIsArchivedView] = useState(false)
  const [pinnedSignals, setPinnedSignals] = useState<
    Map<string, BiothreatSignal>
  >(new Map())
  const [sessionId] = useState(() => Math.random().toString(36).substring(7))
  const { showNullData, setShowNullData } = useUserPreferences()

  const [persistedFilters, setPersistedFilters] =
    usePersistentContext<AlertCurationFilters>(["alert-curation-filters"], {
      serialize: JSON.stringify,
      deserialize: value => {
        const parsed = JSON.parse(value) as AlertCurationFilters & {
          startDate?: string
          endDate?: string
        }

        // START - migration code for old filters
        const oldStartDate = parsed.startDate
        const oldEndDate = parsed.endDate
        delete parsed.startDate
        delete parsed.endDate
        // END - migration code for old filters

        // no date parsing needed if last N days, weeks, months, years selected
        if (isRelative(parsed.dateRange)) {
          return parsed
        }

        return {
          ...parsed,
          dateRange: [
            // dateRange added later but non-optional after new filters are saved
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            safelyParseDate(parsed.dateRange?.[0] ?? oldStartDate),
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            safelyParseDate(parsed.dateRange?.[1] ?? oldEndDate),
          ],
        }
      },
    })

  const alertCurationFilters: AlertCurationFilters = useMemo(
    () => ({
      ...DEFAULT_BIOTHREAT_FILTERS,
      ...persistedFilters,
    }),
    [persistedFilters]
  )

  const saveAndSetAlertCurationFilters = useCallback(
    (filters: AlertCurationFilters) => {
      void setPersistedFilters(filters)
    },
    [setPersistedFilters]
  )

  // Data Fetching
  const { data: diseaseList } = useAlertDiseases()

  const candidateParams = useMemo(() => {
    const dates = getDates(alertCurationFilters.dateRange)

    return {
      sensitivity: alertCurationFilters.sensitivity,
      minScore: alertCurationFilters.aiScoreRange[0],
      disease: alertCurationFilters.disease,
      // TODO: restore when server-side search (and pagination) are implemented
      // search: alertCurationFilters.search,
      startDate: safelyParseDate(dates[0])?.toISOString() ?? "",
      endDate: safelyParseDate(dates[1])?.toISOString() ?? "",
      sessionId,
    }
  }, [
    alertCurationFilters.sensitivity,
    alertCurationFilters.aiScoreRange,
    alertCurationFilters.disease,
    alertCurationFilters.dateRange,
    sessionId,
  ])

  const { data: gdeltData, isLoading: isLoadingGdelt } =
    useAlertCandidates(candidateParams)
  const { data: nwssSignals, isLoading: isLoadingNwss } = useNwssData({
    filters: alertCurationFilters,
  })

  // Mutations
  const triageMutation = useTriageMutation(candidateParams)

  const triage = triageMutation.mutate

  const formattedAlertCandidates = useMemo(
    (): BiothreatSignal[] =>
      gdeltData?.results.map(signal => {
        return {
          id: signal.id,
          source: BiothreatSignalSource.gdelt,
          cluster_id: signal.cluster_id,
          is_centroid: signal.is_centroid,
          is_archived: signal.is_archived,
          cluster_count: signal.cluster_count,
          modern_score: signal.modern_score,
          legacy_score: signal.legacy_score,
          title: signal.title,
          url: signal.url,
          publish_time: signal.publish_time,
          source_name: signal.source_name,
          pruned_themes: signal.pruned_themes,
          threat_category: signal.threat_category ?? null,
          clean_disease_codes: signal.clean_disease_codes ?? [],
          resolved_location: signal.resolved_location ?? "",
          description: null,
          search_term_id: signal.search_term_id,
        }
      }) ?? [],
    [gdeltData]
  )

  const combinedSignals = useMemo((): BiothreatSignal[] => {
    return [...formattedAlertCandidates, ...nwssSignals.results]
  }, [formattedAlertCandidates, nwssSignals])

  const centroidSignals = useMemo<BiothreatSignal[]>(
    () =>
      getCentroidSignals(
        combinedSignals,
        isArchivedView,
        alertCurationFilters.locationFilter,
        alertCurationFilters.type,
        alertCurationFilters.search,
        showNullData
      ),
    [
      combinedSignals,
      isArchivedView,
      alertCurationFilters.locationFilter,
      alertCurationFilters.type,
      alertCurationFilters.search,
      showNullData,
    ]
  )

  // Handlers
  const addToPinnedSignals = useCallback((signal: BiothreatSignal) => {
    setPinnedSignals(prev => {
      const next = new Map(prev)
      next.set(signal.id, { ...signal })
      return next
    })
  }, [])

  const removeFromPinnedSignals = useCallback((id: string) => {
    setPinnedSignals(prev => {
      const next = new Map(prev)
      next.delete(id)
      return next
    })
  }, [])

  const toggleArchivedView = useCallback(() => {
    setIsArchivedView(prev => !prev)
  }, [])

  const value: AlertCurationState = useMemo(
    () => ({
      showAlertCuration,
      isArchivedView,
      toggleArchivedView,
      alertCurationFilters,
      setAlertCurationFilters: saveAndSetAlertCurationFilters,
      diseaseList,
      isLoading: isLoadingGdelt || isLoadingNwss,
      showNullData,
      setShowNullData,
      signals: combinedSignals,
      pinnedSignals,
      centroidSignals,
      triage,
      addToPinnedSignals,
      removeFromPinnedSignals,
    }),
    [
      showAlertCuration,
      isArchivedView,
      toggleArchivedView,
      alertCurationFilters,
      saveAndSetAlertCurationFilters,
      diseaseList,
      isLoadingGdelt,
      isLoadingNwss,
      showNullData,
      setShowNullData,
      combinedSignals,
      pinnedSignals,
      centroidSignals,
      triage,
      addToPinnedSignals,
      removeFromPinnedSignals,
    ]
  )

  return (
    <AlertCurationContext.Provider value={value}>
      {children}
    </AlertCurationContext.Provider>
  )
}
